/**
 * Authentication Service
 * Handles user authentication with JWT tokens
 */

import { AuthCredentials, AuthToken, UserInfo } from '../types';

const TOKEN_STORAGE_KEY = 'remotedesktop_auth_token';
const USER_STORAGE_KEY = 'remotedesktop_user_info';

export interface AuthServiceConfig {
    serverUrl: string;
    tokenRefreshThresholdMs: number;
}

export interface AuthServiceEvents {
    onAuthStateChange?: (isAuthenticated: boolean) => void;
    onTokenRefreshed?: (token: AuthToken) => void;
    onError?: (error: Error) => void;
}

export class AuthService {
    private config: AuthServiceConfig;
    private events: AuthServiceEvents;
    private currentToken: AuthToken | null = null;
    private currentUser: UserInfo | null = null;
    private refreshTimeoutId: number | null = null;

    constructor(config: AuthServiceConfig, events: AuthServiceEvents = {}) {
        this.config = config;
        this.events = events;

        // Load stored auth data
        this.loadStoredAuth();
    }

    /**
     * Login with credentials
     */
    async login(credentials: AuthCredentials): Promise<boolean> {
        try {
            const response = await fetch(`${this.config.serverUrl}/api/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(credentials)
            });

            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Login failed');
            }

            const data = await response.json();

            this.currentToken = {
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiresAt: new Date(data.expiresAt),
                tokenType: data.tokenType || 'Bearer'
            };

            // Get user info
            await this.fetchUserInfo();

            // Store auth data
            this.storeAuth();

            // Schedule token refresh
            this.scheduleTokenRefresh();

            this.events.onAuthStateChange?.(true);
            return true;
        } catch (error) {
            console.error('Login error:', error);
            this.events.onError?.(error as Error);
            return false;
        }
    }

    /**
     * Logout
     */
    async logout(): Promise<void> {
        try {
            if (this.currentToken) {
                // Notify server (optional, may fail if token is invalid)
                await fetch(`${this.config.serverUrl}/api/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${this.currentToken.accessToken}`,
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                }).catch(() => { }); // Ignore errors
            }
        } finally {
            this.clearAuth();
            this.events.onAuthStateChange?.(false);
        }
    }

    /**
     * Refresh the access token
     */
    async refreshToken(): Promise<boolean> {
        if (!this.currentToken?.refreshToken) {
            return false;
        }

        try {
            const response = await fetch(`${this.config.serverUrl}/api/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    refreshToken: this.currentToken.refreshToken
                })
            });

            if (!response.ok) {
                throw new Error('Token refresh failed');
            }

            const data = await response.json();

            this.currentToken = {
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiresAt: new Date(data.expiresAt),
                tokenType: data.tokenType || 'Bearer'
            };

            this.storeAuth();
            this.scheduleTokenRefresh();
            this.events.onTokenRefreshed?.(this.currentToken);

            return true;
        } catch (error) {
            console.error('Token refresh error:', error);
            this.clearAuth();
            this.events.onAuthStateChange?.(false);
            return false;
        }
    }

    /**
     * Get current access token
     */
    getAccessToken(): string | null {
        if (!this.currentToken) return null;

        // Check if token is expired
        if (new Date() >= this.currentToken.expiresAt) {
            return null;
        }

        return this.currentToken.accessToken;
    }

    /**
     * Get current user info
     */
    getUserInfo(): UserInfo | null {
        return this.currentUser;
    }

    /**
     * Check if user is authenticated
     */
    get isAuthenticated(): boolean {
        return this.getAccessToken() !== null;
    }

    /**
     * Check if user has a specific permission
     */
    hasPermission(permission: string): boolean {
        return this.currentUser?.permissions?.includes(permission) ?? false;
    }

    // =========================================================================
    // Private Methods
    // =========================================================================

    private async fetchUserInfo(): Promise<void> {
        if (!this.currentToken) return;

        try {
            const response = await fetch(`${this.config.serverUrl}/api/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${this.currentToken.accessToken}`
                }
            });

            if (response.ok) {
                this.currentUser = await response.json();
            }
        } catch (error) {
            console.error('Failed to fetch user info:', error);
        }
    }

    private scheduleTokenRefresh(): void {
        if (!this.currentToken) return;

        // Clear existing timeout
        if (this.refreshTimeoutId !== null) {
            clearTimeout(this.refreshTimeoutId);
        }

        // Calculate time until refresh needed
        const expiresIn = this.currentToken.expiresAt.getTime() - Date.now();
        const refreshIn = Math.max(0, expiresIn - this.config.tokenRefreshThresholdMs);

        this.refreshTimeoutId = window.setTimeout(() => {
            this.refreshToken();
        }, refreshIn);

        console.log(`Token refresh scheduled in ${Math.round(refreshIn / 1000)}s`);
    }

    private storeAuth(): void {
        if (this.currentToken) {
            localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
                ...this.currentToken,
                expiresAt: this.currentToken.expiresAt.toISOString()
            }));
        }

        if (this.currentUser) {
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(this.currentUser));
        }
    }

    private loadStoredAuth(): void {
        try {
            const tokenData = localStorage.getItem(TOKEN_STORAGE_KEY);
            if (tokenData) {
                const parsed = JSON.parse(tokenData);
                this.currentToken = {
                    ...parsed,
                    expiresAt: new Date(parsed.expiresAt)
                };

                // Check if token is still valid
                if (this.currentToken && new Date() >= this.currentToken.expiresAt) {
                    // Try to refresh
                    this.refreshToken();
                } else {
                    this.scheduleTokenRefresh();
                }
            }

            const userData = localStorage.getItem(USER_STORAGE_KEY);
            if (userData) {
                this.currentUser = JSON.parse(userData);
            }
        } catch (error) {
            console.error('Failed to load stored auth:', error);
            this.clearAuth();
        }
    }

    private clearAuth(): void {
        this.currentToken = null;
        this.currentUser = null;

        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);

        if (this.refreshTimeoutId !== null) {
            clearTimeout(this.refreshTimeoutId);
            this.refreshTimeoutId = null;
        }
    }
}
